import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Shield, ChevronDown, ChevronUp, Plus, ExternalLink, Wrench } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePppcPolicy } from "@/hooks/usePppcPolicy";
import { getPppcServiceDef, isCustomPppcServiceKey } from "@/types/pppc";

interface PrivacyPermissionsCardProps {
  bundleId: string;
  onConfigure?: () => void;
}

export const PrivacyPermissionsCard = ({ bundleId, onConfigure }: PrivacyPermissionsCardProps) => {
  const { data, isLoading } = usePppcPolicy(bundleId);
  const [showFullCodeReq, setShowFullCodeReq] = useState(false);

  const configuredKeys = data
    ? Object.keys(data.choices).filter(
        (k) => data.choices[k] === "allow" || data.choices[k] === "deny",
      )
    : [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Privacy Permissions (PPPC)
            </CardTitle>
            <CardDescription>
              macOS privacy service approvals for this app
            </CardDescription>
          </div>
          <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-xs">
            <Link to="/configurations/pppc">
              Open library <ExternalLink className="h-3 w-3 ml-1" />
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3" aria-busy="true">
            <div className="space-y-1">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3 w-40" />
            </div>
            <div className="space-y-1">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-3 w-full" />
            </div>
            <div className="border-t pt-3 space-y-2">
              <Skeleton className="h-3 w-32" />
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-5 w-12 rounded-full" />
                </div>
              ))}
            </div>
          </div>
        ) : !data || configuredKeys.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground">No privacy permissions configured</p>
            <p className="text-xs text-muted-foreground mt-1">
              Pre-approve or deny privacy services for this app.
            </p>
            {onConfigure && (
              <Button size="sm" className="mt-3" onClick={onConfigure}>
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Configure now
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Bundle ID</p>
              <p className="text-xs font-mono break-all">{data.identifier}</p>
            </div>

            {data.codeRequirement && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Code Requirement</p>
                <p className={`text-xs font-mono break-all ${showFullCodeReq ? "" : "line-clamp-2"}`}>
                  {data.codeRequirement}
                </p>
                {data.codeRequirement.length > 80 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={() => setShowFullCodeReq((v) => !v)}
                  >
                    {showFullCodeReq ? (
                      <>
                        <ChevronUp className="h-3 w-3 mr-1" />
                        Show less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3 mr-1" />
                        Show full
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}

            <div className="border-t pt-3">
              <p className="text-xs text-muted-foreground mb-2">
                {configuredKeys.length} service{configuredKeys.length !== 1 ? "s" : ""} configured
              </p>
              <div className="space-y-1.5">
                {configuredKeys.map((key) => {
                  const choice = data.choices[key];
                  const isCustom = isCustomPppcServiceKey(key);
                  const label = isCustom ? key : getPppcServiceDef(key).label;
                  return (
                    <div key={key} className="flex items-center justify-between gap-2 text-xs">
                      <div className="min-w-0 flex items-center gap-1.5">
                        <span className={isCustom ? "font-mono truncate" : "truncate"}>
                          {label}
                        </span>
                        {isCustom && (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="outline"
                                  className="text-[9px] gap-1 cursor-help border-warning/60 text-warning bg-warning/10 shrink-0"
                                >
                                  <Wrench className="h-2.5 w-2.5" />
                                  Custom
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                Custom TCC service — not in our built-in catalog.
                                Sent to macOS exactly as written.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          choice === "allow"
                            ? "border-success text-success shrink-0"
                            : "border-destructive text-destructive shrink-0"
                        }
                      >
                        {choice === "allow" ? "Allow" : "Deny"}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
